import type { BuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { FacadeTrimBehavior } from "building-types/facades/facadeTypesEnum";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { CornerBoardTrimAlignment } from "building-types/settings/settingsTypesEnum";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import type { StreetOrientation } from "../building/streetOrientationTypesEnum";
import type { DocEntityHandle } from "../handles/docEntityHandleTypes";

export enum ValidCornerCondition {
  INSIDE = "INSIDE",
  OUTSIDE = "OUTSIDE",
}

export enum InvalidCornerCondition {
  COLLINEAR = "COLLINEAR",
}

export type CornerCondition = ValidCornerCondition | InvalidCornerCondition;

export interface CornerBoardBehavior {
  readonly edgeBehavior1?: FacadeTrimBehavior;
  readonly edgeBehavior2?: FacadeTrimBehavior;
}

export interface CornerBoardConfig {
  readonly outsideBehavior: CornerBoardBehavior;
  readonly insideBehavior: CornerBoardBehavior;
}

export interface CornerBoardRail {
  readonly cornerCondition: ValidCornerCondition;
  readonly rail: PolyLine3;
  readonly profileId?: string;
  readonly streetOrientation?: StreetOrientation;
}

export interface CornerBoards extends BuildingEntityBase<"CORNER_BOARDS"> {
  readonly levelId: string;
  readonly faces: FaceArrayViewEntity[];
  readonly rails: CornerBoardRail[];
}

export interface CornerBoardRefPointOptions {
  readonly ax: number;
  readonly ay: number;
  readonly bx: number;
  readonly by: number;
  readonly cx: number;
  readonly cy: number;
  readonly wallOffsetEdge1: number;
  readonly wallOffsetEdge2: number;
}

export interface CornerBoardOffsetParams {
  readonly topAlignment: CornerBoardTrimAlignment;
  readonly topOffset: number;
  readonly bottomAlignment: CornerBoardTrimAlignment;
  readonly bottomOffset: number;
}

export interface CornerBoardProfileOptions extends CornerBoardRefPointOptions, CornerBoardOffsetParams {
  readonly profile: PolyLine2;
  readonly profileId: string;
  readonly material: ExteriorMaterial | undefined;
  readonly width: number;
  readonly thickness: number;
  readonly cornerCondition: ValidCornerCondition;
  readonly faceArrays: FaceArrayViewEntity[];
  readonly cornerBoardRails: CornerBoardRail[];
  readonly viewEntityDescriptor: ViewEntityDescriptor;
  readonly floorIndex: number;
  readonly entityHandleForSelectingReportingUnit?: DocEntityHandle;
  readonly pickHandle?: DocEntityHandle;
  readonly streetOrientationEdge1?: StreetOrientation;
  readonly streetOrientationEdge2?: StreetOrientation;

  // these are mutated
  hasBoardEdge1: boolean;
  hasBoardEdge2: boolean;
  startZ: number;
  endZ: number;
}
